/**
 * Root shell · sketch-theme app frame
 *
 * Strict copy of design/canvas-first-v5.html § app-topbar:
 *   dots(mac风) + app-title + crumb + right (avatars / agent badges)
 *
 * Design HTML uses --ink/--paper/--hand short names · SPA tokens.css uses
 * full semantic names (--fg-default / --bg-base / --font-body etc).
 */

import { createRootRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useMemo, type CSSProperties } from 'react';

export const Route = createRootRoute({
  component: RootShell,
});

interface SessionUser {
  user_id: string;
  email: string;
  display_name?: string;
}

export interface ParsedWorkbenchPath {
  kind: 'home' | 'workspace' | 'agents' | 'canvas' | 'other';
  workspaceId: string | null;
  sessionId: string | null;
}

export interface TopbarCrumbItem {
  label: string;
  target:
    | { kind: 'home' }
    | { kind: 'workspace'; workspaceId: string }
    | null;
}

export function readUser(): SessionUser | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem('aiw.spa.user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionUser;
  } catch {
    return null;
  }
}

export function readWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('aiw.spa.workspace_id');
}

export function clearSpaSession(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('aiw.spa.token');
  sessionStorage.removeItem('aiw.spa.user');
  sessionStorage.removeItem('aiw.spa.workspace_id');
}

export function parseWorkbenchPath(path: string): ParsedWorkbenchPath {
  if (path === '/') return { kind: 'home', workspaceId: null, sessionId: null };
  const canvas = path.match(/^\/workspace\/([^/]+)\/canvas\/([^/]+)$/);
  if (canvas) {
    return {
      kind: 'canvas',
      workspaceId: decodeURIComponent(canvas[1]),
      sessionId: decodeURIComponent(canvas[2]),
    };
  }
  const agents = path.match(/^\/workspace\/([^/]+)\/agents$/);
  if (agents) {
    return {
      kind: 'agents',
      workspaceId: decodeURIComponent(agents[1]),
      sessionId: null,
    };
  }
  const workspace = path.match(/^\/workspace\/([^/]+)$/);
  if (workspace) {
    return {
      kind: 'workspace',
      workspaceId: decodeURIComponent(workspace[1]),
      sessionId: null,
    };
  }
  return { kind: 'other', workspaceId: null, sessionId: null };
}

export function topbarHomeTarget(storedWorkspaceId: string | null): { kind: 'home' } | { kind: 'workspace'; workspaceId: string } {
  return storedWorkspaceId ? { kind: 'workspace', workspaceId: storedWorkspaceId } : { kind: 'home' };
}

export function topbarCrumbsForPath(path: string): TopbarCrumbItem[] {
  const parsed = parseWorkbenchPath(path);
  if (parsed.kind === 'home') {
    return [{ label: 'home', target: null }];
  }
  if (parsed.kind === 'workspace' && parsed.workspaceId) {
    return [{ label: `workspace · ${shortId(parsed.workspaceId)}`, target: null }];
  }
  if (parsed.kind === 'agents' && parsed.workspaceId) {
    return [
      { label: `workspace · ${shortId(parsed.workspaceId)}`, target: { kind: 'workspace', workspaceId: parsed.workspaceId } },
      { label: 'agent 设置', target: null },
    ];
  }
  if (parsed.kind === 'canvas' && parsed.workspaceId && parsed.sessionId) {
    return [
      { label: `workspace · ${shortId(parsed.workspaceId)}`, target: { kind: 'workspace', workspaceId: parsed.workspaceId } },
      { label: `canvas · ${shortId(parsed.sessionId)}`, target: null },
    ];
  }
  return [{ label: path, target: null }];
}

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8)}…` : value;
}

function userInitial(u: SessionUser | null): string {
  if (!u) return '?';
  const name = u.display_name?.trim() || u.email;
  return (name[0] || '?').toUpperCase();
}

function RootShell(): JSX.Element {
  const navigate = useNavigate();
  // useRouterState with `select` returns a stable string · whole-state read
  // would trigger render every navigation tick. Pathname is enough for our
  // crumb + isLogin check.
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Re-read sessionStorage when path changes (login → workspace transition).
  // useMemo + path dep avoids the setState loop the previous useEffect caused
  // (readUser returned a fresh object on every call → setState → re-render →
  // effect runs again).
  const user = useMemo(() => readUser(), [path]);
  const workspaceId = useMemo(() => readWorkspaceId(), [path]);
  const homeTarget = topbarHomeTarget(workspaceId);
  const crumbs = topbarCrumbsForPath(path);
  const isLogin = path === '/login';

  function navigateTarget(target: Exclude<TopbarCrumbItem['target'], null>): void {
    if (target.kind === 'home') {
      navigate({ to: '/' });
      return;
    }
    navigate({ to: '/workspace/$workspaceId', params: { workspaceId: target.workspaceId } });
  }

  function handleLogout(): void {
    clearSpaSession();
    navigate({ to: '/login' });
  }

  return (
    <div style={shellStyle}>
      {!isLogin && (
        <header style={topbarStyle}>
          <div style={dotsStyle} aria-hidden="true">
            <span style={dotStyle} />
            <span style={dotStyle} />
            <span style={dotStyle} />
          </div>
          <button
            type="button"
            style={appTitleButtonStyle}
            onClick={() => navigateTarget(homeTarget)}
            data-testid="topbar-home"
            title={homeTarget.kind === 'workspace' ? '回到工作区' : '回到首页'}
          >
            aiw · workbench
          </button>
          <nav style={crumbNavStyle} aria-label="当前位置" data-testid="topbar-crumbs">
            {crumbs.map((crumb, idx) => (
              <span key={`${crumb.label}-${idx}`} style={crumbItemWrapStyle}>
                {idx > 0 && <span style={crumbSepStyle}>/</span>}
                {crumb.target ? (
                  <button
                    type="button"
                    style={crumbButtonStyle}
                    onClick={() => navigateTarget(crumb.target!)}
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span style={crumbStyle}>{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
          <div style={rightStyle}>
            {/* Agent badges: α (claude-code) · β / γ unused in v0.1 */}
            <span style={{ ...avatarStyle, ...avatarBotStyle }} title="Claude Code · α">α</span>
            {user && (
              <button
                type="button"
                style={{ ...avatarStyle, ...avatarA1Style, cursor: 'pointer' }}
                title={`${user.email} · 退出登录`}
                aria-label="退出登录"
                data-testid="topbar-logout"
                onClick={handleLogout}
              >
                {userInitial(user)}
              </button>
            )}
          </div>
        </header>
      )}
      <main style={isLogin ? mainLoginStyle : mainStyle}>
        <Outlet />
      </main>
    </div>
  );
}

const shellStyle: CSSProperties = {
  minHeight: '100vh',
  background: 'var(--bg-base)',
  color: 'var(--fg-default)',
  display: 'flex',
  flexDirection: 'column',
};

const topbarStyle: CSSProperties = {
  height: 44,
  padding: '0 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  background: 'var(--bg-subtle)',
  borderBottom: '1.5px solid var(--border-default)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  flexShrink: 0,
};

const dotsStyle: CSSProperties = {
  display: 'flex',
  gap: 5,
};

const dotStyle: CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  border: '1.2px solid var(--border-default)',
  background: 'var(--bg-surface)',
  display: 'inline-block',
};

const appTitleButtonStyle: CSSProperties = {
  fontFamily: 'var(--font-title)',
  fontWeight: 700,
  fontSize: 18,
  color: 'var(--fg-default)',
  border: 0,
  padding: 0,
  background: 'transparent',
  cursor: 'pointer',
};

const crumbStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-faint)',
};

const crumbNavStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  gap: 4,
};

const crumbItemWrapStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  minWidth: 0,
};

const crumbSepStyle: CSSProperties = {
  color: 'var(--fg-faint)',
  opacity: 0.7,
};

const crumbButtonStyle: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  color: 'var(--fg-soft)',
  border: 0,
  padding: 0,
  background: 'transparent',
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
};

const rightStyle: CSSProperties = {
  marginLeft: 'auto',
  display: 'flex',
  gap: 4,
  alignItems: 'center',
};

const avatarStyle: CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: '50%',
  border: '1.3px solid var(--border-default)',
  background: 'var(--bg-surface)',
  fontFamily: 'var(--font-title)',
  fontSize: 14,
  fontWeight: 700,
  display: 'grid',
  placeItems: 'center',
  marginLeft: -6,
  color: 'var(--fg-default)',
  padding: 0,
};

const avatarBotStyle: CSSProperties = {
  background: 'var(--fg-default)',
  color: 'var(--bg-surface)',
  fontSize: 13,
};

const avatarA1Style: CSSProperties = {
  background: 'var(--accent-warning-bg)',
};

const mainStyle: CSSProperties = {
  flex: 1,
  padding: 'var(--space-12)',
  minHeight: 0,
  overflow: 'auto',
};

const mainLoginStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  minHeight: 0,
};
